import { ApolloError } from 'apollo-client'
import {
  Card,
  CardHeader,
  GreyIconWrapper,
  IconButton,
  PaperclipIcon,
  SpinnerButton,
  Theme,
  createStyles,
  makeStyles,
  useSnackbar,
} from '@forgeglobal/design-framework'
import { uploadFile } from 'util/fileUtils'
import { useDropzone } from 'react-dropzone'
import React, { ReactNode, useState } from 'react'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dropping: {
      border: `5px dashed ${theme.palette.divider}`,
      padding: `${theme.spacing(2) - 5}px`,
    },
    cardContentPadding: {
      padding: theme.spacing(2),
    },
  })
)

interface FileUploaderCardProps {
  acceptedFileTypes: string
  title: ReactNode
  rightTitle?: ReactNode
  uploadCaption?: string
  isPaperclipVisible?: boolean
  onFileSerials: (fileSerials: string[]) => Promise<any>
}

export const FileUploaderCard: React.FC<FileUploaderCardProps> = ({
  acceptedFileTypes,
  title,
  rightTitle,
  uploadCaption,
  isPaperclipVisible,
  onFileSerials,
  children,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles()
  const [isDropping, setIsDropping] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: acceptedFileTypes,
    onDropRejected: (rejectedFiles: File[]) => {
      rejectedFiles.forEach(file => {
        alert(`${file.name} is not a valid document. We only currently support ${acceptedFileTypes} files.`)
      })
    },
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setIsUploading(true)
        enqueueSnackbar(
          acceptedFiles.length === 1
            ? `Adding file ${acceptedFiles[0].name} ...`
            : `Adding ${acceptedFiles.length} files ...`
        )
        const fileSerialsPromise = acceptedFiles.map(async file => {
          const serial = await uploadFile(file)
          enqueueSnackbar(`File ${file.name} added.`)
          return serial
        })
        Promise.all(fileSerialsPromise)
          .then(serials => onFileSerials(serials))
          .then(() => setIsUploading(false))
          .catch(error => {
            // Be sure to signal uploading is no longer ongoing
            // Apollo errors are implicitly reported at app level (others should be handled here)
            if (!(error instanceof ApolloError)) enqueueSnackbar(`An error occurred: ${error}`, { variant: 'error' })
            setIsUploading(false)
          })
      }
      setIsDropping(false)
    },
    onDragEnter: () => {
      setIsDropping(true)
    },
    onDragLeave: () => {
      setIsDropping(false)
    },
  })

  return (
    <Card>
      <CardHeader
        title={title}
        rightTitle={rightTitle}
        action={
          isPaperclipVisible && (
            <IconButton>
              <GreyIconWrapper>
                <PaperclipIcon onClick={open} />
              </GreyIconWrapper>
            </IconButton>
          )
        }
      />
      <div className={isDropping ? classes.dropping : classes.cardContentPadding} {...(getRootProps() as any)}>
        <input {...(getInputProps() as any)} />
        {children}
        <SpinnerButton onClick={open} fullWidth loading={isUploading} caption={uploadCaption ?? 'Upload Documents'} />
      </div>
    </Card>
  )
}
